import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Autocomplete,
  Popover,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import BusinessIcon from '@mui/icons-material/Business';
import { getIndianCities } from '../../../utils/helper';
const DEFAULT_CONSTRUCTION_STATUS_OPTIONS = [
  'All',
  'Pre-Launch',
  'New Launch',
  'Under Construction',
  'Near Possession',
  'Ready to Move',
  'Completed',
];
const CATEGORY_OPTIONS = ['All', 'Residential', 'Commercial', 'Retail', 'Luxury', 'Plots', 'Farmhouse', 'Warehouse', 'Farm Land'];

const LOCATION_OPTIONS = getIndianCities().map((c) => `${c.city}, ${c.state}`);

const filterLocationOptions = (options, { inputValue }) => {
  if (!inputValue || inputValue.length < 2) return [];
  const q = inputValue.toLowerCase().trim();
  return options.filter((opt) => opt.toLowerCase().includes(q));
};

/**
 * Projects toolbar: SearchBar (project name/location), Budget, Location, Construction Status, Category,
 * Builder, Import icon button, + New Project button.
 */
const ProjectsToolbar = ({
  search = '',
  onSearchChange,
  budgetMin = '',
  budgetMax = '',
  onBudgetMinChange,
  onBudgetMaxChange,
  locationFilter = '',
  onLocationChange,
  constructionStatus = 'All',
  onConstructionStatusChange,
  category = 'All',
  onCategoryChange,
  builderFilter = '',
  onBuilderChange,
  builderOptions = [],
  onImport,
  onNewProject,
  constructionStatusOptions,
  categoryOptions,
}) => {
  const [budgetAnchor, setBudgetAnchor] = useState(null);
  const budgetActive = budgetMin !== '' || budgetMax !== '';
  const constructionOptions = constructionStatusOptions ?? DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
  const categoryOpts = categoryOptions ?? CATEGORY_OPTIONS;
  const builderOpts = [{ value: '', label: 'All' }, ...builderOptions.map((d) => ({ value: String(d.id), label: d.name || `Developer ${d.id}` }))];

  const hasActiveFilters =
    budgetActive ||
    (locationFilter && locationFilter.trim() !== '') ||
    category !== 'All' ||
    constructionStatus !== 'All' ||
    (builderFilter !== '' && builderFilter != null);

  const clearAllFilters = () => {
    onBudgetMinChange?.('');
    onBudgetMaxChange?.('');
    onLocationChange?.('');
    onCategoryChange?.('All');
    onConstructionStatusChange?.('All');
    onBuilderChange?.('');
  };

  const locationValue = locationFilter && locationFilter.trim() !== '' ? locationFilter : null;

  const budgetLabel = budgetActive
    ? `₹${budgetMin || '0'} L – ₹${budgetMax || '∞'} L`
    : 'Budget';

  return (
    <Box>
      {/* Row 1: Search | Budget | Location | Construction Status | Category | Import icon | + New Project */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setBudgetAnchor(e.currentTarget)}
          sx={{
            minWidth: 140,
            justifyContent: 'space-between',
            textTransform: 'none',
            ...(budgetActive && {
              bgcolor: 'rgba(46, 125, 50, 0.08)',
              borderColor: 'success.main',
              color: 'success.dark',
              '&:hover': { borderColor: 'success.dark', bgcolor: 'rgba(46, 125, 50, 0.12)' },
            }),
          }}
        >
          {budgetLabel}
        </Button>
        <Popover
          open={Boolean(budgetAnchor)}
          anchorEl={budgetAnchor}
          onClose={() => setBudgetAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 1.5, p: 2, minWidth: 220 } } }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
            Budget (₹ Lakh)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Enter min/max in Lakh (L). e.g. 90 = ₹90 L, 100 = ₹1 Cr</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="small"
              label="Min (₹ Lakh)"
              type="number"
              placeholder="e.g. 90"
              value={budgetMin}
              onChange={(e) => onBudgetMinChange?.(e.target.value === '' ? '' : e.target.value)}
              inputProps={{ min: 0, step: 10 }}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
              }}
            />
            <TextField
              size="small"
              label="Max (₹ Lakh)"
              type="number"
              placeholder="e.g. 200"
              value={budgetMax}
              onChange={(e) => onBudgetMaxChange?.(e.target.value === '' ? '' : e.target.value)}
              inputProps={{ min: 0, step: 10 }}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
              }}
            />
          </Box>
        </Popover>
        <Autocomplete
          size="small"
          freeSolo
          options={LOCATION_OPTIONS}
          value={locationValue}
          onChange={(_, newValue) => onLocationChange?.(typeof newValue === 'string' ? newValue : newValue ?? '')}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
          filterOptions={filterLocationOptions}
          isOptionEqualToValue={(opt, val) => opt === val}
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder="Type city or state to search..." />
          )}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Construction Status</InputLabel>
          <Select
            value={constructionStatus}
            label="Construction Status"
            onChange={(e) => onConstructionStatusChange?.(e.target.value)}
          >
            {constructionOptions.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(e) => onCategoryChange?.(e.target.value)}>
            {categoryOpts.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Builder</InputLabel>
          <Select
            value={builderFilter === '' || builderFilter == null ? '' : String(builderFilter)}
            label="Builder"
            onChange={(e) => onBuilderChange?.(e.target.value === '' ? '' : e.target.value)}
          >
            {builderOpts.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewProject}
            size="small"
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* Row 2: Applied filter chips + Clear all */}
      {hasActiveFilters && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
          {budgetActive && (
            <Chip
              size="small"
              icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: 'success.dark' }} />}
              label={`Budget: ₹${budgetMin || '0'} L – ₹${budgetMax || '∞'} L`}
              onDelete={() => { onBudgetMinChange?.(''); onBudgetMaxChange?.(''); }}
              sx={{
                bgcolor: 'rgba(46, 125, 50, 0.12)',
                color: 'success.dark',
                '& .MuiChip-deleteIcon': { color: 'success.dark' },
              }}
            />
          )}
          {locationValue && (
            <Chip
              size="small"
              icon={<LocationOnIcon sx={{ fontSize: 16, ml: 0.5 }} />}
              label={`Location: ${locationValue}`}
              onDelete={() => onLocationChange?.('')}
              variant="outlined"
            />
          )}
          {constructionStatus !== 'All' && (
            <Chip
              size="small"
              icon={<HomeIcon sx={{ fontSize: 16, ml: 0.5 }} />}
              label={constructionStatus}
              onDelete={() => onConstructionStatusChange?.('All')}
              variant="outlined"
            />
          )}
          {category !== 'All' && (
            <Chip
              size="small"
              icon={<CategoryIcon sx={{ fontSize: 16, ml: 0.5 }} />}
              label={`Category: ${category}`}
              onDelete={() => onCategoryChange?.('All')}
              variant="outlined"
            />
          )}
          {builderFilter !== '' && builderFilter != null && (
            <Chip
              size="small"
              icon={<BusinessIcon sx={{ fontSize: 16, ml: 0.5 }} />}
              label={`Builder: ${builderOpts.find((o) => o.value === String(builderFilter))?.label || builderFilter}`}
              onDelete={() => onBuilderChange?.('')}
              variant="outlined"
            />
          )}
          <Typography
            component="button"
            type="button"
            onClick={clearAllFilters}
            sx={{
              fontSize: '0.875rem',
              color: 'text.primary',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProjectsToolbar;
export {
  DEFAULT_CONSTRUCTION_STATUS_OPTIONS as CONSTRUCTION_STATUS_OPTIONS,
  CATEGORY_OPTIONS as CATEGORY_OPTIONS,
  LOCATION_OPTIONS,
};
